'use strict';

/**
 * @ngdoc service
 * @name vTvPackageServices.TvPackageApi
 * @description Creates standard calls to the mule api and manages caching
 */

angular.module('v-tv-package-services').service('TvPackageApi', function TvPackageApi($injector, $http, $q, $translate, tvPackageConfig, CacheFactory, promiseTracker, $interpolate) {
    var _this = this;

    this.defaultHeaders = {
        'content-type': 'application/json',
        'x-locale': $translate.use()
    };

    var apiPromiseTracker = promiseTracker();
    var apiCache = new CacheFactory('TvPackageApi', { storeOnResolve: true });
    var onGoingProms = {};

    // http://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript-jquery
    /* eslint-disable */
    var hashCode = function hashCode(input) {
        var hash = 0,
            i = void 0,
            chr = void 0,
            len = void 0;

        if (input.length === 0) return hash;
        for (i = 0, len = input.length; i < len; i++) {
            chr = input.charCodeAt(i);
            hash = (hash << 5) - hash + chr;
            hash |= 0;
        }
        return hash;
    };
    /* eslint-enable */

    /**
     * @ngdoc method
     * @private
     * @name vTvPackageServices.TvPackageApi#callApi
     * @methodOf vTvPackageServices.TvPackageApi
     * @param {object} config $http configuration
     * @param {boolean=true} useCache use a cache based on the call signature
     * @returns {Promise} A promise that resolves/rejects when the data is loaded
     */

    var callApi = function callApi(config, useCache) {
        var headers = void 0;
        var cacheKey = void 0;

        if (angular.isDefined(config.headers)) {
            config.headers = config.headers || {};
            headers = angular.extend(angular.copy(_this.defaultHeaders), config.headers);
        } else {
            headers = _this.defaultHeaders;
        }

        config.headers = headers;
        config.cache = false;
        config.tracker = apiPromiseTracker;
        config.params = config.params || {};

        /* eslint-disable camelcase */
        config.params.client_id = config.params.client_id || 'd096372d504a4ab293988c06921d37ef';
        config.params.client_secret = config.params.client_secret || 'f3c36c7ceab64027BF977B581397CBF6';
        /* eslint-enable camelcase */

        cacheKey = hashCode(angular.toJson(config));

        if (useCache && apiCache.get(cacheKey)) {
            return $q.when(apiCache.get(cacheKey));
        }

        if (angular.isDefined(onGoingProms[cacheKey])) {
            return onGoingProms[cacheKey];
        }

        onGoingProms[cacheKey] = $http(config).then(function (response) {
            apiCache.put(cacheKey, response);

            return response;
        }, function (errorResponse) {

            return $q.reject(errorResponse);
        }).finally(function () {
            delete onGoingProms[cacheKey];
        });

        return onGoingProms[cacheKey];
    };

    /**
     * @ngdoc method
     * @private
     * @name vTvPackageServices.TvPackageApi#getUrl
     * @methodOf vTvPackageServices.TvPackageApi
     * @param {String} endpoint of the api, concatenated on the base URL. can contain interpolation tag for domainName and packageId
     * @returns {String} the full url of the api endpoint
     */

    function getUrl(endpoint) {
        var TvPackageSelectionStateService = $injector.get('TvPackageSelectionStateService');
        var exp = $interpolate(endpoint);

        return tvPackageConfig.API_URL + exp({
            domainName: TvPackageSelectionStateService.getDomain(),
            packageId: TvPackageSelectionStateService.getPackageId()
        });
    }

    /**
     * @ngdoc method
     * @name vTvPackageServices.TvPackageApi#doGet
     * @methodOf vTvPackageServices.TvPackageApi
     * @description Sends a GET request to the tv package API
     * @param {String} endpoint of the api, concatenated on the base URL. can contain interpolation tag for domainName and packageId
     * @param {Object} params a key/value pair that is to be appended in the querystring
     * @param {Object} headers a list of custom headers to pass the the $http query
     * @param {Boolean=true} useCache If this call should be cached or not
     * @returns {Promise} A promise that resolves/rejects when the request is completed
     */

    this.doGet = function (endpoint, params, headers, useCache) {
        return callApi({
            method: 'GET',
            url: getUrl(endpoint),
            params: params,
            headers: headers
        }, useCache);
    };

    /**
     * @ngdoc method
     * @name vTvPackageServices.TvPackageApi#doPost
     * @methodOf vTvPackageServices.TvPackageApi
     * @description Sends a PUT request to the tv package API (creates a new resource or submit changes to a resource)
     * @param {String} endpoint of the api, concatenated on the base URL. can contain interpolation tag for domainName and packageId
     * @param {Object} params a key/value pair that is to be appended in the querystring
     * @param {Object} data An object to send into the post body
     * @param {Object} headers a list of custom headers to pass the the $http query
     * @param {Boolean=true} useCache If this call should be cached or not
     * @returns {Promise} A promise that resolves/rejects when the request is completed
     */

    this.doPost = function (endpoint, params, data, headers, useCache) {
        return callApi({
            method: 'POST',
            url: getUrl(endpoint),
            params: params,
            data: data,
            headers: headers
        }, useCache);
    };

    /**
     * @ngdoc method
     * @name vTvPackageServices.TvPackageApi#doPut
     * @methodOf vTvPackageServices.TvPackageApi
     * @description Sends a PUT request to the tv package API (edit an existing resource)
     * @param {String} endpoint of the api, concatenated on the base URL. can contain interpolation tag for domainName and packageId
     * @param {Object} params a key/value pair that is to be appended in the querystring
     * @param {Object} data An object to send into the post body
     * @param {Object} headers a list of custom headers to pass the the $http query
     * @returns {Promise} A promise that resolves/rejects when the request is completed
     */

    this.doPut = function (endpoint, params, data, headers) {
        return callApi({
            method: 'PUT',
            url: getUrl(endpoint),
            params: params,
            data: data,
            headers: headers
        });
    };

    /**
     * @ngdoc method
     * @name vTvPackageServices.TvPackageApi#doPatch
     * @methodOf vTvPackageServices.TvPackageApi
     * @description Sends a PATCH request to the tv package API (edit part of an existing resource)
     * @param {String} endpoint of the api, concatenated on the base URL. can contain interpolation tag for domainName and packageId
     * @param {Object} params a key/value pair that is to be appended in the querystring
     * @param {Object} data An object to send into the post body
     * @param {Object} headers a list of custom headers to pass the the $http query
     * @returns {Promise} A promise that resolves/rejects when the request is completed
     */

    this.doPatch = function (endpoint, params, data, headers) {
        return callApi({
            method: 'PATCH',
            url: getUrl(endpoint),
            params: params,
            data: data,
            headers: headers
        });
    };

    /**
     * @name vTvPackageServices.TvPackageApi#isTrackingActive
     * @methodOf vTvPackageServices.TvPackageApi
     * @description Allows to determine if the service is currently waiting for a call result and that the call is
     * long enough to be considered noticeable or "active"
     * (see activationDelay of https://github.com/ajoslin/angular-promise-tracker#api-documentation)
     * @returns {Boolean} true if the service is waiting for a call promise to complete since a noticeable
     * period of time
     */

    this.isTrackingActive = function () {
        return apiPromiseTracker.active();
    };

    /**
     * @name vTvPackageServices.TvPackageApi#isTrackingActive
     * @methodOf vTvPackageServices.TvPackageApi
     * @description Allows to determine if the service is currently waiting for a call result
     * @returns {Boolean} true if the service is waiting for a call promise to complete
     */
    this.isTracking = function () {
        return apiPromiseTracker.tracking();
    };
});